(() => {
  const starters = [
    { sku: "CRL12", length: 3750 },
    { sku: "CRL14", length: 4350 },
    { sku: "CRL16", length: 4950 },
    { sku: "CRL18", length: 5550 },
  ];
  const extensions = [
    { sku: "CRLE4", length: 1200 },
    { sku: "CRLE6", length: 1800 },
    { sku: "CRLE8", length: 2400 },
  ];

  function millimetres(value) {
    const text = String(value ?? "").trim();
    if (!/^\d+(?:\.\d{1,3})?$/.test(text)) return null;
    const result = Math.round(Number(text) * 1000);
    return Number.isSafeInteger(result) && result > 0 ? result : null;
  }

  function displayMetres(value) {
    return `${(Math.ceil(value / 100) / 10).toFixed(1)} m`;
  }

  function requiredMillimetres(value) {
    const text = String(value ?? "").trim();
    if (!/^\d+(?:\.\d+)?$/.test(text)) return null;
    const [whole, fraction = ""] = text.split(".");
    const tenths = Number(whole) * 10 + Number(fraction[0] || 0) + (/[1-9]/.test(fraction.slice(1)) ? 1 : 0);
    const result = tenths * 100;
    return Number.isSafeInteger(result) && result > 0 ? result : null;
  }

  function productImage(product, variant) {
    const image = variant.featured_image || product.featured_image || product.images?.[0];
    const source = typeof image === "string" ? image : image?.src;
    if (!source) return null;
    try {
      const url = new URL(source, "https://cdn.shopify.com");
      if (url.protocol !== "https:") return null;
      url.searchParams.set("width", "360");
      return url.href;
    } catch (error) {
      return null;
    }
  }

  function recommend({ required, piece }) {
    if (!Number.isSafeInteger(required) || required <= 0 ||
        !Number.isSafeInteger(piece) || piece < 2400 || piece >= 3000) return null;
    let best = null;
    for (const starter of starters) {
      if (starter.length - 2400 > piece) continue;
      // A valid hook-piece limit admits all three extensions. In 600 mm units,
      // n extensions can make every total from 2n to 4n. Solve directly so the
      // number of sections is unrestricted without an unbounded search loop.
      const deficit = Math.max(0, required - starter.length);
      const added = Math.ceil(deficit / 2400);
      const units = Math.max(2 * added, Math.ceil(deficit / 600));
      const length = starter.length + units * 600;
      if (!Number.isSafeInteger(length)) continue;
      if (best && (added > best.added || (added === best.added && length >= best.length))) continue;
      // Preserve workbook ties: smallest CRLE4 count, then CRLE6 count.
      const reduction = 4 * added - units;
      const a = Math.max(0, reduction - added);
      const b = reduction - 2 * a;
      const quantities = [a, b, added - a - b];
      best = {
        starter, added, length, excess: length - required,
        items: [
          { sku: starter.sku, quantity: 1, length: starter.length },
          ...extensions.map((item, i) => ({ ...item, quantity: quantities[i] })).filter((item) => item.quantity),
        ],
      };
    }
    return best;
  }

  function priceConfiguration(result, products) {
    let total = 0;
    const issues = [];
    const items = result.items.map((item, index) => {
      const product = products[index === 0 ? 0 : 1];
      const matches = (product?.variants || []).filter((variant) => variant.sku === item.sku);
      const variant = matches.length === 1 ? matches[0] : null;
      if (!variant || !Number.isSafeInteger(variant.price) || variant.price < 0) {
        issues.push(`${item.sku} could not be matched to a unique priced product. Contact us for help.`);
        return { ...item, variant: null };
      }
      total += variant.price * item.quantity;
      if (!variant.available) issues.push(`${item.sku} is currently unavailable.`);
      return { ...item, variant, title: product.title, handle: product.handle, image: productImage(product, variant) };
    });
    if (new Set(items.filter((item) => item.variant).map((item) => item.variant.id)).size !== items.filter((item) => item.variant).length) {
      issues.push("Product configuration needs checking. Contact us for help.");
    }
    if (!Number.isSafeInteger(total)) issues.push("This quantity needs a quotation. Contact us for pricing.");
    return { items, total: Number.isSafeInteger(total) && items.every((item) => item.variant) ? total : null, issues };
  }

  function purchaseAllowed(result, priced) {
    return Boolean(result && priced && !priced.issues.length);
  }

  // The calculation is also exercised directly by the local Node test suite.
  if (typeof module !== "undefined" && module.exports) {
    module.exports = { millimetres, requiredMillimetres, displayMetres, productImage, recommend, priceConfiguration, purchaseAllowed };
  }
  if (typeof customElements === "undefined" || customElements.get("roof-ladder-calculator")) return;

  class RoofLadderCalculator extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.root = window.Shopify?.routes?.root || "/";
      this.pieceLimit = millimetres(this.dataset.pieceLimit);
      this.inputs = [...this.querySelectorAll("input")];
      this.button = this.querySelector("[data-add]");
      this.status = this.querySelector("[data-status]");
      this.cartStatus = this.querySelector("[data-cart-status]");
      this.money = new Intl.NumberFormat(document.documentElement.lang || "en-GB", {
        style: "currency", currency: this.dataset.currency || "GBP",
      });
      this.inputs.forEach((input) => input.addEventListener("input", () => {
        this.hasEdited = true;
        // Keep partial entries editable, but never leave extra decimal places visible.
        if (/\.\d{2,}$/.test(input.value)) this.normaliseRequired();
        this.render();
      }));
      this.querySelector("[data-required]").addEventListener("blur", () => this.normaliseRequired());
      this.button.addEventListener("click", () => this.addConfiguration());
      this.querySelector("[data-reload]").addEventListener("click", () => this.loadProducts());
      this.querySelector("[data-status-action]").addEventListener("click", () => {
        if (this.statusAction === "retry") this.loadProducts();
        else if (this.statusAction === "input") this.querySelector("[data-required]").focus();
        else if (this.statusAction === "contact") window.location.assign(this.querySelector("[data-enquiry]").href);
        else if (this.statusAction === "cart") window.location.assign(this.querySelector("[data-cart-link]").href);
        else {
          const heading = this.querySelector("[data-configuration-heading]");
          heading.scrollIntoView({ block: "start" });
          heading.focus({ preventScroll: true });
        }
      });
      if (typeof IntersectionObserver !== "undefined") {
        const input = this.querySelector("[data-required]");
        this.promptObserver = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          if (!input.value && document.activeElement !== input) input.classList.add("roof-calculator__input--prompt");
          this.promptObserver.disconnect();
        }, { threshold: 0.75 });
        this.promptObserver.observe(input);
      }
      this.loadProducts();
    }

    disconnectedCallback() {
      this.promptObserver?.disconnect();
    }

    async fetchProducts() {
      return Promise.all([this.dataset.starterUrl, this.dataset.extensionUrl].map(async (path) => {
        if (!path) throw new Error("Products are not configured.");
        const url = new URL(path, window.location.origin);
        if (url.origin !== window.location.origin) throw new Error("Invalid product URL.");
        url.pathname += ".js";
        url.search = "";
        const response = await fetch(url, { cache: "no-store", signal: AbortSignal.timeout(15000) });
        if (!response.ok) throw new Error("Product prices could not be loaded.");
        const product = await response.json();
        if (!Array.isArray(product.variants)) throw new Error("Invalid product data.");
        return product;
      }));
    }

    async loadProducts() {
      if (this.loading || this.busy) return;
      this.loading = true;
      this.productError = false;
      this.render();
      try {
        this.products = await this.fetchProducts();
      } catch (error) {
        this.products = null;
        this.productError = true;
      } finally {
        this.loading = false;
        this.render();
      }
    }

    normaliseRequired() {
      const input = this.querySelector("[data-required]");
      const required = requiredMillimetres(input.value);
      if (required) input.value = (required / 1000).toFixed(1);
    }

    render() {
      const required = requiredMillimetres(this.querySelector("[data-required]").value);
      const piece = this.pieceLimit;
      const validPiece = piece && piece >= 2400 && piece < 3000;
      const valid = required && validPiece;
      const empty = !this.querySelector("[data-required]").value;
      this.querySelector("[data-required]").setAttribute("aria-invalid", String(!required && Boolean(this.hasEdited)));
      this.result = valid ? recommend({ required, piece }) : null;
      this.priced = this.result && this.products ? priceConfiguration(this.result, this.products) : null;
      this.querySelector("[data-result]").hidden = !this.result;
      this.querySelector("[data-reload]").hidden = !this.productError;
      this.querySelector("[data-reload]").disabled = this.loading || this.busy;
      this.button.disabled = this.busy || this.loading || this.cartUncertain || !purchaseAllowed(this.result, this.priced);
      this.button.hidden = !this.result;
      const messages = [];
      if (!validPiece) messages.push("Online recommendations are temporarily unavailable. Please contact us for help choosing your ladder.");
      else if (!valid) messages.push("Enter your roof slope length in metres, greater than zero. We round it up to one decimal place.");
      else if (!this.result) messages.push("We cannot calculate a configuration for these measurements. Contact us for help.");
      if (this.result) {
        messages.push(`For your ${this.metres(required)} roof slope, this configuration ${this.result.excess ? `is ${Number((this.result.excess / 10).toFixed(1))} cm longer than your entry because sections come in fixed sizes. Check that this fits your roof and follow the product instructions. Extra length does not block purchase.` : "matches the required length."}`);
        if (this.loading) messages.push("Checking current prices and availability…");
        if (this.productError) messages.push("Current prices could not be loaded. Refresh prices or contact us.");
        if (this.priced) messages.push(...this.priced.issues);
        if (!messages.length) messages.push("Your recommended configuration is ready.");
        this.querySelector("[data-total-length]").textContent = this.metres(this.result.length);
        this.querySelector("[data-excess]").textContent = this.metres(this.result.excess);
        this.querySelector("[data-included]").textContent = `${this.result.starter.sku} includes one 2.4 m roof-hook section and one ${this.metres(this.result.starter.length - 2400)} starter extension. ${this.result.added === 0 ? "No extra extensions needed." : `${this.result.added} extra extension${this.result.added === 1 ? "" : "s"} needed.`}`;
        this.querySelector("[data-price]").textContent = this.priced?.total != null ? this.money.format(this.priced.total / 100) : "Unavailable";
        const list = this.querySelector("[data-products]");
        list.replaceChildren();
        (this.priced?.items || this.result.items).forEach((item, index) => {
          const row = document.createElement("li");
          row.className = "roof-calculator__product";
          const media = document.createElement("div");
          media.className = "roof-calculator__media";
          const placeholder = document.createElement("span");
          placeholder.className = "roof-calculator__image-placeholder";
          placeholder.textContent = item.sku;
          media.append(placeholder);
          if (item.image) {
            const image = document.createElement("img");
            image.alt = `${item.title} — ${item.sku}`;
            image.width = 180;
            image.height = 180;
            image.loading = "lazy";
            image.decoding = "async";
            image.addEventListener("load", () => { placeholder.hidden = true; });
            image.addEventListener("error", () => { image.hidden = true; placeholder.hidden = false; });
            image.src = item.image;
            media.append(image);
          }
          row.append(media);
          const details = document.createElement("div");
          details.className = "roof-calculator__product-details";
          const category = document.createElement("p");
          category.className = "roof-calculator__product-category";
          category.textContent = index === 0 ? "Starter ladder · hook included" : "Additional extension";
          details.append(category);
          const label = document.createElement(item.variant ? "a" : "span");
          label.className = "roof-calculator__product-title";
          label.textContent = `${item.title || (index === 0 ? "Roof ladder" : "Roof ladder extension")} · ${item.sku}`;
          if (item.variant) label.href = `${this.root}products/${encodeURIComponent(item.handle)}?variant=${item.variant.id}`;
          details.append(label);
          const dimensions = document.createElement("p");
          dimensions.className = "roof-calculator__product-dimensions";
          dimensions.textContent = `${this.metres(item.length)} · Quantity ${item.quantity}`;
          details.append(dimensions);
          const price = document.createElement("span");
          price.className = "roof-calculator__product-price";
          price.textContent = item.variant ? `${this.money.format(item.variant.price / 100)} each · ${this.money.format(item.variant.price * item.quantity / 100)} (Ex VAT)${item.variant.available ? "" : " · Unavailable"}` : "Price unavailable";
          details.append(price);
          row.append(details);
          list.append(row);
        });
      }
      this.status.textContent = messages.join(" ");
      let state = "ready";
      let heading = "Your recommended configuration is ready.";
      let next = `Next: review your products and ${this.priced?.total != null ? this.money.format(this.priced.total / 100) + " (Ex VAT) total" : "price"}, then add the complete configuration to your basket.`;
      let action = "view";
      let label = "View configuration and price";
      if (this.cartUncertain) {
        state = "blocked"; heading = "Check your basket before continuing.";
        this.status.textContent = this.cartStatus.textContent;
        next = "Next: open your basket and check which items were added before trying again.";
        action = "cart"; label = "Review your basket";
      } else if (empty && !this.hasEdited) {
        state = "empty"; heading = "Start with your roof slope length.";
        this.status.textContent = "Enter your measurement above to find your starter ladder and extensions.";
        next = "Next: measure along the slope from eaves to ridge, then enter the length in metres.";
        action = "input"; label = "Enter your roof length";
      } else if (this.busy || this.loading) {
        state = "loading"; heading = this.busy ? "Adding your configuration…" : "Checking your configuration…";
        next = "Please wait. We are checking current prices and availability.";
        action = null;
      } else if (!validPiece || (valid && !this.result)) {
        state = "blocked"; heading = "We cannot recommend a configuration yet.";
        next = "Next: contact our team with your roof slope measurement for help choosing the products.";
        action = "contact"; label = "Get help with this configuration";
      } else if (!required) {
        state = "blocked"; heading = "Enter a valid measurement to continue.";
        next = "Next: enter a roof slope length greater than zero in the field above.";
        action = "input"; label = "Edit roof slope length";
      } else if (this.productError || !this.priced) {
        state = "blocked"; heading = "Your configuration is not ready to buy.";
        next = "Next: refresh prices to check availability and enable the basket button.";
        action = "retry"; label = "Refresh prices and availability";
      } else if (this.priced.issues.length) {
        state = "blocked"; heading = "Your configuration is not ready to buy.";
        this.status.textContent = this.priced.issues.join(" ");
        next = "Next: contact our team about the products listed above. Changing your measurement will not resolve this product issue.";
        action = "contact"; label = "Get help with these products";
      }
      this.querySelector("[data-status-card]").dataset.state = state;
      this.querySelector("[data-status-heading]").textContent = heading;
      this.querySelector("[data-status-next]").textContent = next;
      this.statusAction = action;
      const statusButton = this.querySelector("[data-status-action]");
      statusButton.hidden = !action;
      statusButton.textContent = label;
      this.querySelector("[data-enquiry]").hidden = action !== "contact";
    }

    metres(value) {
      return displayMetres(value);
    }

    async refreshCart(cart) {
      const response = await fetch(`${this.root}cart.js`, { cache: "no-store", signal: AbortSignal.timeout(15000) });
      if (!response.ok) throw new Error("Basket refresh failed.");
      await response.json();
      if (cart) {
        const sections = await cart.reload();
        if (cart.getSectionsToRender().some(({ id }) => !sections[id])) throw new Error("Basket sections could not be refreshed.");
      }
    }

    async addConfiguration() {
      if (this.button.disabled || this.busy) return;
      this.normaliseRequired();
      this.busy = true;
      this.inputs.forEach((input) => { input.disabled = true; });
      this.button.setAttribute("aria-busy", "true");
      this.cartStatus.textContent = "Checking current prices…";
      const previous = this.priced;
      const cart = document.querySelector("cart-sidebar");
      let attempted = false;
      let added = false;
      this.render();
      try {
        this.products = await this.fetchProducts();
        this.render();
        if (!purchaseAllowed(this.result, this.priced)) {
          this.cartStatus.textContent = "Availability has changed. Please review the configuration.";
          return;
        }
        const changed = this.priced.items.some((item, i) => item.variant.id !== previous.items[i].variant.id || item.variant.price !== previous.items[i].variant.price);
        if (changed) {
          this.cartStatus.textContent = "Product details or prices have changed. Review the updated total, then add the configuration again.";
          return;
        }
        const payload = { items: this.priced.items.map((item) => ({ id: item.variant.id, quantity: item.quantity })) };
        if (cart) {
          cart.setActiveElement(this.button);
          payload.sections = cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
        }
        this.cartStatus.textContent = "Adding your configuration…";
        attempted = true;
        const response = await fetch(`${this.root}cart/add.js`, {
          method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify(payload), signal: AbortSignal.timeout(20000),
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || "The configuration could not be added.");
        added = true;
        if (cart) {
          if (payload.sections.every((id) => data.sections?.[id])) cart.renderContents(data);
          else {
            await this.refreshCart(cart);
            cart.revealStickyHeaderAndOpen() || cart.open();
          }
        }
        this.cartStatus.textContent = "Complete configuration added to your basket.";
        this.querySelector("[data-cart-link]").hidden = false;
      } catch (error) {
        if (attempted) {
          // Shopify can partially add stock on an error; never retry this POST automatically.
          this.cartUncertain = true;
          this.querySelector("[data-cart-link]").hidden = false;
          this.cartStatus.textContent = added ? "Items were added, but the basket display could not update. Review your basket before adding anything else." : `${error.message} Some items may already be in your basket. Review it before trying again.`;
          try {
            await this.refreshCart(cart);
          } catch (refreshError) {
            this.cartStatus.textContent += " Open your basket to see its latest contents.";
          } finally {
            cart?.classList.remove("updating");
          }
        } else {
          this.products = null;
          this.productError = true;
          this.cartStatus.textContent = "Prices could not be verified. Nothing was added. Refresh prices and try again.";
        }
      } finally {
        this.busy = false;
        this.inputs.forEach((input) => { input.disabled = false; });
        this.button.removeAttribute("aria-busy");
        this.render();
      }
    }
  }

  customElements.define("roof-ladder-calculator", RoofLadderCalculator);
})();
